import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { DASH } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [faceRefs, setFaceRefs] = useState([]);

  useEffect(() => {
    api.get("/history?limit=6").then((r) => setItems(r.data.items || [])).catch(() => {});
    api
      .get("/face-references")
      .then((r) => setFaceRefs((r.data.items || []).filter((f) => f.thumbnail_path)))
      .catch(() => {});
  }, []);

  const remaining = user?.generation_remaining ?? 0;
  const limit = user?.generation_limit;
  const used = user?.generation_used ?? 0;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const planLabel = user?.has_byok ? "Own key" : "No key";

  const completed = items.filter((i) => i.status === "completed").length;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 bg-zinc-950 flex items-center justify-center">
              <span className="font-serif text-white text-2xl leading-none">Æ</span>
            </div>
            <div className="leading-none">
              <p className="font-serif text-[19px] tracking-tight text-zinc-950">
                AI Photo & Content Studio
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                Product · Model · Editorial
              </p>
            </div>
          </div>

          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            Welcome back, {user?.name?.split(" ")[0] || "Stylist"}
          </p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-zinc-950">
            The studio is <span className="italic text-zinc-500">ready.</span>
          </h1>

          {faceRefs.length > 0 && (
            <div className="mt-10">
              <div className="flex items-baseline justify-between">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Model library
                </h2>
                <Link
                  to="/create"
                  className="text-[12px] font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-950"
                >
                  Pick a model →
                </Link>
              </div>
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                {faceRefs.slice(0, 8).map((ref) => (
                  <Link
                    key={ref.ref_id}
                    to={`/create?face_ref=${encodeURIComponent(ref.ref_id)}`}
                    className="group relative aspect-[4/5] overflow-hidden bg-zinc-200"
                  >
                    <AuthImage width={320}
                      path={ref.thumbnail_path}
                      alt={ref.name || "Model"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/75 via-black/20 to-transparent px-2 pb-2 pt-8">
                      <span className="text-[11px] text-white truncate">{ref.name}</span>
                      <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] text-white/80">
                        Use
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Remaining Generations card hidden per request */}
        </div>

        <div className="lg:col-span-5" data-testid={DASH.quickStats}>
          <div className="grid grid-cols-2 gap-4">
            <StatCard label="Used" value={used} />
            <StatCard label="Completed" value={completed} />
            <StatCard label="Plan" value={planLabel} mono />
          </div>
          <div className="mt-6 border border-zinc-200 p-6 bg-zinc-50">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              How it works
            </p>
            <ol className="mt-4 space-y-3 text-[13px] text-zinc-700">
              <li>1. Upload a product photo (JPG · PNG · WEBP · ≤ 10 MB)</li>
              <li>2. Pick model, pose, background, aspect ratio</li>
              <li>3. AI Photo & Content Studio renders a photorealistic editorial image</li>
              <li>4. Download in high resolution</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="mt-16 grid lg:grid-cols-2 gap-4">
        <Link
          to="/create"
          className="border border-zinc-200 bg-white p-6 hover:border-zinc-950 transition-colors"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Studio 01
          </p>
          <p className="mt-2 font-serif text-2xl text-zinc-950">Photo Studio</p>
          <p className="mt-2 text-[13px] text-zinc-600">
            Upload 1 foto produk, pilih model dan framing, generate 1 foto atau 6-foto
            series dalam satu sesi.
          </p>
        </Link>
        <Link
          to="/content"
          className="border border-zinc-200 bg-white p-6 hover:border-zinc-950 transition-colors"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Studio 02
          </p>
          <p className="mt-2 font-serif text-2xl text-zinc-950">Content Studio</p>
          <p className="mt-2 text-[13px] text-zinc-600">
            Ubah hasil generate jadi paket konten Instagram: caption per slide, caption
            utama, hashtag, alt text, dan download ZIP siap post.
          </p>
        </Link>
      </div>

      <div className="mt-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">Recent renders</h2>
          <Link to="/history" className="text-[12px] font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-950">
            View all →
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="border border-dashed border-zinc-300 py-16 text-center">
            <p className="text-zinc-500 text-sm">No generations yet. Create your first one.</p>
          </div>
        ) : (
          <div data-testid={DASH.recentGrid} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {items.map((it) => (
              <Link key={it.job_id} to="/history" className="aspect-[4/5] overflow-hidden bg-zinc-100 group">
                {it.generated_path ? (
                  <AuthImage width={320}
                    path={it.generated_path}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-mono uppercase text-zinc-400">
                    {it.status}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, mono }) {
  return (
    <div className="border border-zinc-200 p-5 bg-white">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</p>
      <p className={`mt-3 ${mono ? "font-mono text-lg" : "font-serif text-3xl"} text-zinc-950`}>
        {value}
        {suffix && <span className="text-zinc-400 text-lg ml-1">{suffix}</span>}
      </p>
    </div>
  );
}
